import { CodeLinkProvider, CodeLinkTarget, CodeRange, createRange } from "./codeLinks";

/**
 * `{{include "thread_name" [accounting]}}` / `{{include_once ...}}` macro of the ETL preprocessing
 * (p_ETL_EVAL_MACROS) found in a document.
 */
export type SqlIncludeMacro = {
	command: 'include' | 'include_once',
	/** name (DESCRIPTION) of the included thread, without the quotes */
	name: string,
	/**
	 * second parameter of the macro: the accounting the thread is looked up in; undefined when omitted (the
	 * macro then resolves in the accounting it runs under) or when it is not an integer
	 */
	accounting?: number,
	/** range of the thread name (the quotes excluded) */
	range: CodeRange,
	/** range of the whole `{{...}}` macro */
	macroRange: CodeRange,
};

const INCLUDE_COMMANDS = new Set(['include', 'include_once']);

/**
 * Finds the include macros of the content the way p_ETL_EVAL_MACROS parses them: `{{...}}` may span lines,
 * the body splits into whitespace separated tokens, a `"quoted"` token may contain spaces. The command is
 * matched without regard to case, like the SQL collation does.
 */
export function findIncludeMacros(content: string): SqlIncludeMacro[] {
	const macros: SqlIncludeMacro[] = [];
	if (!content) return macros;

	const macroRegex = /\{\{([\n\s\S]*?)\}\}/g;
	let macro: RegExpExecArray | null;
	while ((macro = macroRegex.exec(content)) !== null) {
		const bodyOffset = macro.index + 2;
		const tokens: { text: string, quoted: boolean, start: number, end: number }[] = [];
		const tokenRegex = /"([^"]*)"|\S+/g;
		let token: RegExpExecArray | null;
		while ((token = tokenRegex.exec(macro[1])) !== null) {
			const quoted = token[1] !== undefined;
			tokens.push({
				text: quoted ? token[1] : token[0],
				quoted,
				start: bodyOffset + token.index + (quoted ? 1 : 0),
				end: bodyOffset + token.index + token[0].length - (quoted ? 1 : 0),
			});
		}

		const command = tokens[0]?.text.toLowerCase();
		const name = tokens[1];
		if (!command || !INCLUDE_COMMANDS.has(command) || !name?.text) continue;

		const accounting = tokens[2]?.text;
		macros.push({
			command: command as SqlIncludeMacro['command'],
			name: name.text,
			accounting: accounting !== undefined && /^-?\d+$/.test(accounting) ? parseInt(accounting) : undefined,
			range: createRange(content, name.start, name.end),
			macroRange: createRange(content, macro.index, macro.index + macro[0].length),
		});
	}
	return macros;
}

export type SqlIncludeLinkOptions = {
	/** hover text of the link, e.g. "Open thread" */
	tooltip?: string,
	/** Ctrl+click on the thread name; the callback opens the thread and reports a missing one itself */
	open: (macro: SqlIncludeMacro) => void | Promise<void>,
};

/** Link provider that turns the thread names of `{{include}}` / `{{include_once}}` macros into links. */
export function createSqlIncludeLinkProvider(options: SqlIncludeLinkOptions): CodeLinkProvider {
	return {
		provide: (content: string): CodeLinkTarget[] => findIncludeMacros(content).map(macro => ({
			range: macro.range,
			tooltip: options.tooltip,
			open: () => options.open(macro),
		})),
	};
}
